import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt } from "./reportUtils";

interface CashFlowReportProps {
  period: string;
}

const CashFlowReport: React.FC<CashFlowReportProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reportsApi.getCashFlow(currentBuilding.id, period);
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching cashflow:", err);
        if (!cancelled)
          setError("Failed to load cashflow report. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading cashflow report..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!report) {
    return <ErrorAlert message="No cashflow data available" />;
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Cashflow Statement
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {report.startDate} to {report.endDate}
      </p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Opening Balance
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-gray-900">
              {fmt(report.openingBalance || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Total Inflows
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-green-600">
              {fmt(report.totalInflows || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Total Outflows
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-red-600">
              {fmt(report.totalOutflows || 0)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Net Flow
            </dt>
            <dd
              className={`mt-2 text-2xl font-semibold ${
                report.totalInflows - report.totalOutflows >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {fmt((report.totalInflows || 0) - (report.totalOutflows || 0))}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-xs font-medium text-gray-500 uppercase">
              Closing Balance
            </dt>
            <dd className="mt-2 text-2xl font-semibold text-gray-900">
              {fmt(report.closingBalance || 0)}
            </dd>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium text-gray-900">
            Monthly Breakdown
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Month
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opening Balance
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inflows
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Outflows
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Flow
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Closing Balance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(report.months || []).map((month: any, idx: number) => (
                <tr key={idx}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {month.month}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                    {fmt(month.openingBalance || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {fmt(month.inflows || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                    {fmt(month.outflows || 0)}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      month.netFlow >= 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {fmt(month.netFlow || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                    {fmt(month.closingBalance || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CashFlowReport;
